import React, { useState } from "react";
import "./App.css";
import useEMICalculation from "./hooks/useEMICalculation";
import useSIPCalculation from "./hooks/useSIPCalculation";
import LoanInputSection from "./components/LoanInputSection";
import PrepaymentSection from "./components/PrepaymentSection";
import SummarySection from "./components/SummarySection";
import LoanCharts from "./components/LoanCharts";
import AmortizationTable from "./components/AmortizationTable";
import SIPTab from "./components/SIPTab";

const EnhancedCalculator = () => {
  const [activeTab, setActiveTab] = useState("loan");

  const emiState = useEMICalculation();
  const sipState = useSIPCalculation();

  const { schedule, summary, loanPeriod, interestRate, rateType, monthlyExtraPayment } = emiState;

  const chartData = schedule.map((row) => ({
    month: row.month,
    principal: row.principal,
    interest: row.interest,
    extraPayment: row.extraPayment,
    lumpsumPrepayment: row.lumpsumPrepayment,
    balance: row.balance,
    rate: row.rate || interestRate,
    rateChanged: row.rateChanged || false,
  }));

  const copyLoanToSip = (calculationType) => {
    sipState.setSipPeriod(loanPeriod);
    const totalInterest = Math.round(summary.totalInterest);
    const totalAmountPayable = Math.round(summary.totalPayable);
    const targetAmount = calculationType === "total" ? totalAmountPayable : totalInterest;
    const requiredSip = sipState.calculateSIP(targetAmount, sipState.sipReturnRate, loanPeriod);
    sipState.setSipTarget(targetAmount);
    sipState.setSipAmount(Math.round(requiredSip * 100) / 100);
    sipState.setSipCalculationType(calculationType);
    setActiveTab("sip");
  };

  return (
    <div className="calculator-container">
      {/* Header */}
      <header className="app-header">
        <div className="app-header-badge">
          <span>✦</span> Financial Planning Tool
        </div>
        <h1 className="title">
          <span>Financial</span> Calculator Suite
        </h1>
        <p className="app-subtitle">
          Plan your loan, simulate prepayments, and compare SIP investment alternatives
        </p>
      </header>

      {/* Tab Navigation */}
      <nav className="tab-nav" role="tablist" aria-label="Calculator sections">
        <button
          role="tab"
          aria-selected={activeTab === "loan"}
          className={`tab-btn${activeTab === "loan" ? " active-loan" : ""}`}
          onClick={() => setActiveTab("loan")}
        >
          🏦 Loan Management
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "sip"}
          className={`tab-btn${activeTab === "sip" ? " active-sip" : ""}`}
          onClick={() => setActiveTab("sip")}
        >
          📈 SIP Calculator
        </button>
      </nav>

      {/* Loan Management Tab */}
      {activeTab === "loan" && (
        <div role="tabpanel" aria-label="Loan Management">
          <div className="two-col-grid">
            <LoanInputSection emiState={emiState} />
            <PrepaymentSection
              emiState={emiState}
              chartData={chartData}
              summary={summary}
              copyLoanToSip={copyLoanToSip}
            />
          </div>

          <SummarySection
            summary={summary}
            monthlyExtraPayment={monthlyExtraPayment}
            rateType={rateType}
          />

          <LoanCharts chartData={chartData} />

          <AmortizationTable
            schedule={schedule}
            rateType={rateType}
            interestRate={interestRate}
          />
        </div>
      )}

      {/* SIP Alternative Tab */}
      {activeTab === "sip" && (
        <div role="tabpanel" aria-label="SIP Alternative Calculator">
          <SIPTab sipState={sipState} />
        </div>
      )}
    </div>
  );
};

export default EnhancedCalculator;
